import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  LinearProgress,
  Typography,
  Paper,
  Button,
  TextField,
  Box,
  CircularProgress,
  Snackbar,
  Alert,
  useTheme,
  useMediaQuery,
  InputAdornment,
  IconButton,
  alpha
} from "@mui/material";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SendIcon from "@mui/icons-material/Send";
import LinkIcon from "@mui/icons-material/Link";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";

import axiosInstance from "../api/axiosConfig";
import {
  normalizeDownloadUrl,
  downloadEndpoint,
  downloadErrorMessage,
  isPlaylistUrl,
  siteLabel,
} from "../utils/downloadUtils";

interface VideoUploaderProps {
  onVideoListChange: () => void;
}

const VideoUploader: React.FC<VideoUploaderProps> = ({ onVideoListChange }) => {
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadSpeed, setUploadSpeed] = useState(0);
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [notification, setNotification] = useState<{message: string, severity: 'success' | 'error'} | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const extractErrorMessage = (err: any) => {
    return err?.response?.data?.error || err?.response?.data?.message || err?.message || String(err);
  };

  const handlePasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) setYoutubeUrl(text);
    } catch (err) {
      console.error("Failed to read clipboard contents: ", err);
      setNotification({ message: "Failed to read from clipboard", severity: 'error' });
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.type.startsWith('video/')) {
        const formData = new FormData();
        formData.append('video', file);
        uploadFile(formData);
      } else {
        setNotification({ message: "Please upload a valid video file", severity: 'error' });
      }
    }
  };

  const uploadFile = (formData: FormData) => {
    const startTime = Date.now();
    let lastLoaded = 0;
    let lastTime = startTime;
    setUploadProgress(0);
    setUploadSpeed(0);
    setIsUploading(true);

    axiosInstance
      .post(`${backendUrl}/api/upload`, formData, {
        onUploadProgress: (progressEvent) => {
          try {
            if (progressEvent.total) {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setUploadProgress(percentCompleted);
              const currentTime = Date.now();
              const deltaSec = Math.max(0.001, (currentTime - lastTime) / 1000);
              const loadedBytes = progressEvent.loaded - lastLoaded;
              const speed = loadedBytes / deltaSec;
              setUploadSpeed(speed / 1024 / 1024);
              lastLoaded = progressEvent.loaded;
              lastTime = currentTime;
            }
          } catch (e) { }
        },
      })
      .then((response) => {
        setNotification({ message: "Video uploaded successfully!", severity: 'success' });
        onVideoListChange();
      })
      .catch((error: any) => {
        const errMsg = extractErrorMessage(error) || 'Upload failed. Please try again.';
        setNotification({ message: errMsg, severity: 'error' });
      })
      .finally(() => {
        setIsUploading(false);
      });
  };

  const handleYoutubeUpload = async () => {
    const url = normalizeDownloadUrl(youtubeUrl);
    if (!url) {
      setNotification({
        message: "Paste a full video link, e.g. https://x.com/user/status/123",
        severity: "error",
      });
      return;
    }
    setIsLoading(true);

    try {
      if (isPlaylistUrl(url)) {
        setNotification({ 
          message: "Playlist download is currently disabled.", 
          severity: 'error' 
        });
        setIsLoading(false);
        setYoutubeUrl("");
        return;
      }

      const response = await axiosInstance.post(downloadEndpoint(url, backendUrl), { url });

      if (response.data.exist) {
        navigate(`/video/${response.data.videoId}`);
      } else {
        setNotification({ message: `${siteLabel(url)} video processing...`, severity: 'success' });
        onVideoListChange();
      }
    } catch (error: any) {
      setNotification({ 
        message: downloadErrorMessage(error, url), 
        severity: 'error' 
      });
    } finally {
      setIsLoading(false);
      setYoutubeUrl("");
    }
  };

  return (
    <Paper 
      elevation={0}
      sx={{ 
        p: isExpanded ? { xs: 2, sm: 2.5 } : { xs: 1.5, sm: 2 }, 
        borderRadius: 4,
        bgcolor: alpha(theme.palette.background.paper, 0.8),
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(255,255,255,0.1)',

        transition: 'all 0.3s ease',
        ...(isMobile && {
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          borderRadius: '24px 24px 0 0',
          borderTop: '1px solid rgba(255,255,255,0.1)',
          boxShadow: '0 -10px 40px rgba(0,0,0,0.5)',
          ...(isExpanded && {
            maxHeight: '65vh',
            overflowY: 'auto'
          })
        })
      }}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="video/*"
        hidden
        onChange={(e) => {
          if (e.target.files && e.target.files[0]) {
            const formData = new FormData();
            formData.append('video', e.target.files[0]);
            uploadFile(formData);
          }
        }}
      />

      {!isExpanded ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 }}>
          {/* Mobile: pill drag handle at top — tap/drag up to expand */}
          {isMobile && (
            <Box
              onClick={() => setIsExpanded(true)}
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                pb: 1,
                pt: 0.25,
                cursor: 'pointer',
                touchAction: 'none'
              }}
            >
              <Box sx={{
                width: 40,
                height: 4,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.text.primary, 0.25),
                transition: 'background-color 0.2s',
                '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.45) }
              }} />
            </Box>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1.5 }, width: '100%' }}>
            <Button
              variant="outlined"
              onClick={() => fileInputRef.current?.click()}
              size="small"
              sx={{ borderRadius: 2, flexShrink: 0, height: 36, px: { xs: 1, sm: 2 } }}
            >
              {isMobile ? <CloudUploadIcon fontSize="small" /> : "Upload File"}
            </Button>
            <TextField
              size="small"
              fullWidth
              placeholder="Paste YouTube or video URL..."
              value={youtubeUrl}
              onChange={(e) => setYoutubeUrl(e.target.value)}
              disabled={isLoading}
              InputProps={{
                sx: { borderRadius: 2, height: 36, fontSize: { xs: '16px', sm: '0.875rem' } },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handlePasteFromClipboard} size="small" edge="end">
                      <ContentPasteIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <Button
              variant="contained"
              disabled={isLoading || !youtubeUrl}
              onClick={handleYoutubeUpload}
              size="small"
              sx={{ borderRadius: 2, minWidth: isMobile ? 40 : 80, height: 36, flexShrink: 0, boxShadow: 'none' }}
            >
              {isLoading ? <CircularProgress size={16} color="inherit" /> : <SendIcon fontSize="small" />}
            </Button>
          </Box>

          {/* Desktop: small chevron below the input row to expand downward */}
          {!isMobile && (
            <Box
              onClick={() => setIsExpanded(true)}
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                pb: 0.25,
                pt: 1,
                cursor: 'pointer',
                touchAction: 'none'
              }}
            >
              <Box sx={{
                width: 40,
                height: 4,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.text.primary, 0.25),
                transition: 'background-color 0.2s',
                '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.45) }
              }} />
            </Box>
          )}
        </Box>

      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {/* Mobile: pill drag handle at top — tap/drag down to collapse */}
          {isMobile && (
            <Box
              onClick={() => setIsExpanded(false)}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                pb: 1.5,
                pt: 0.25,
                cursor: 'pointer',
                touchAction: 'none'
              }}
            >
              <Box sx={{
                width: 40,
                height: 4,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.text.primary, 0.25),
                transition: 'background-color 0.2s',
                '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.45) }
              }} />
            </Box>
          )}

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
            {/* Upload Area */}
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: { xs: 2, sm: 2.5 },
                border: '2px dashed',
                borderColor: dragActive ? 'primary.main' : alpha(theme.palette.text.primary, 0.1),
                borderRadius: 3,
                bgcolor: dragActive ? alpha(theme.palette.primary.main, 0.05) : 'transparent',
                transition: 'all 0.2s',
                cursor: 'pointer',
                minHeight: { xs: 80, sm: 100 },
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: alpha(theme.palette.primary.main, 0.02)
                }
              }}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              {isUploading ? (
                <Box sx={{ width: '100%', textAlign: 'center' }}>
                  <LinearProgress variant="determinate" value={uploadProgress} sx={{ mb: 1.5, height: 6, borderRadius: 4 }} />
                  <Typography variant="body2" color="text.secondary">
                    Uploading: {uploadProgress}% • {uploadSpeed.toFixed(1)} MB/s
                  </Typography>
                </Box>
              ) : (
                <>
                  <CloudUploadIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: 'primary.main', mb: 1, opacity: 0.8 }} />
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.25 }}>
                    Click or Drop Video
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    MP4, MKV, AVI, WEBM
                  </Typography>
                </>
              )}
            </Box>

            {/* URL Input Area */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 1.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <LinkIcon color="primary" fontSize="small" /> Import from URL
              </Typography>

              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  size="small"
                  variant="outlined"
                  fullWidth
                  value={youtubeUrl}
                  onChange={(e) => setYoutubeUrl(e.target.value)}
                  placeholder="Paste YouTube or video URL..."
                  disabled={isLoading}
                  InputProps={{
                    sx: { borderRadius: 2, fontSize: { xs: '16px', sm: '0.875rem' } },
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={handlePasteFromClipboard} size="small">
                          <ContentPasteIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
                <Button
                  variant="contained"
                  disabled={isLoading || !youtubeUrl}
                  onClick={handleYoutubeUpload}
                  size="small"
                  sx={{
                    borderRadius: 2,
                    px: 2,
                    minWidth: 80,
                    boxShadow: 'none',
                    '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }
                  }}
                >
                  {isLoading ? <CircularProgress size={20} color="inherit" /> : <SendIcon fontSize="small" />}
                </Button>
              </Box>

              <Typography variant="caption" color="text.secondary">
                Supports YouTube and direct video links. Playlist download is disabled.
              </Typography>
            </Box>
          </Box>

          {/* Desktop: chevron at bottom to collapse upward */}
          {!isMobile && (
            <Box
              onClick={() => setIsExpanded(false)}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                pb: 0.25,
                pt: 1,
                cursor: 'pointer',
                touchAction: 'none'
              }}
            >
              <Box sx={{
                width: 40,
                height: 4,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.text.primary, 0.25),
                transition: 'background-color 0.2s',
                '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.45) }
              }} />
            </Box>
          )}
        </Box>
      )}

      <Snackbar 
        open={!!notification} 
        autoHideDuration={4000} 
        onClose={() => setNotification(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setNotification(null)} 
          severity={notification?.severity} 
          variant="filled" 
          sx={{ width: '100%', borderRadius: 2 }}
        >
          {notification?.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default VideoUploader;